import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Database, CreditCard, BarChart3, Activity, Search,
  Clock, Layers, TrendingUp, Gauge, Coins, Server, HardDrive
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function MemoryAnalyticsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [memorySummary, setMemorySummary] = useState(null);
  const [entitySearch, setEntitySearch] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileData = await authService.getProfile();
      setUser(profileData.member);

      const [creditsRes, usageRes, breakdownRes, memoryRes] = await Promise.allSettled([
        apiClient.get('/api/v1/billing/credits'),
        apiClient.get('/api/v1/billing/usage'),
        apiClient.get('/api/v1/billing/usage/breakdown'),
        apiClient.get('/api/v1/memory/summary'),
      ]);

      setCredits(creditsRes.status === 'fulfilled' ? creditsRes.value.data : {
        balance: 4280,
        total: 10000,
        used: 5720,
        nextRefill: '2026-04-01',
      });

      setUsage(usageRes.status === 'fulfilled' ? usageRes.value.data : {
        currentMonth: 5720,
        allocated: 10000,
        outcomeFees: 142.50,
        tokenConsumed: 1284000,
      });

      setBreakdown(breakdownRes.status === 'fulfilled' ? breakdownRes.value.data?.endpoints || [] : [
        { endpoint: '/api/v1/intelligence/predictions', calls: 1240, credits: 1860, trend: [40, 55, 48, 62, 58, 70, 65] },
        { endpoint: '/api/v1/seo/audit', calls: 890, credits: 1335, trend: [30, 28, 35, 42, 38, 44, 50] },
        { endpoint: '/api/v1/channels/publish', calls: 620, credits: 620, trend: [20, 22, 18, 25, 30, 28, 32] },
        { endpoint: '/api/v1/global/countries', calls: 440, credits: 440, trend: [15, 12, 18, 14, 20, 16, 22] },
        { endpoint: '/api/v1/funding/dashboard', calls: 380, credits: 570, trend: [10, 14, 12, 16, 18, 15, 20] },
        { endpoint: '/api/v1/memory/query', calls: 310, credits: 465, trend: [8, 12, 10, 14, 11, 16, 18] },
        { endpoint: '/api/v1/security/trust', calls: 220, credits: 330, trend: [5, 8, 6, 10, 7, 9, 12] },
      ]);

      setMemorySummary(memoryRes.status === 'fulfilled' ? memoryRes.value.data : {
        totalEntities: 2847,
        domains: 12,
        lastUpdated: '2026-03-06T10:30:00Z',
        storageUsed: '1.2 GB',
        entities: [
          { id: 'e1', name: 'Product Catalog', type: 'domain', records: 1240, lastAccess: '2m ago' },
          { id: 'e2', name: 'Customer Profiles', type: 'domain', records: 856, lastAccess: '5m ago' },
          { id: 'e3', name: 'Order History', type: 'domain', records: 412, lastAccess: '12m ago' },
          { id: 'e4', name: 'Channel Config', type: 'domain', records: 189, lastAccess: '1h ago' },
          { id: 'e5', name: 'Price Rules', type: 'domain', records: 150, lastAccess: '3h ago' },
        ],
      });
    } catch (error) {
      console.error('Failed to load memory analytics:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const handleEntitySearch = (e) => {
    e.preventDefault();
    if (!entitySearch.trim()) return;
    const found = memorySummary?.entities?.find(
      (ent) => ent.name.toLowerCase().includes(entitySearch.toLowerCase())
    );
    if (found) {
      setSelectedEntity(found);
      toast.success('Entity found', { description: found.name });
    } else {
      toast.error('Entity not found', { description: `No results for "${entitySearch}"` });
    }
  };

  const creditPercent = credits ? Math.round((credits.used / credits.total) * 100) : 0;

  const Sparkline = ({ data, color = '#3EF1C7' }) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const width = 80;
    const height = 24;
    const points = data.map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');
    return (
      <svg width={width} height={height} className="inline-block">
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading memory analytics...</p>
        </div>
      </div>
    );
  }

  const monthlyData = [
    { month: 'Oct', allocated: 10000, used: 4200 },
    { month: 'Nov', allocated: 10000, used: 5100 },
    { month: 'Dec', allocated: 10000, used: 6800 },
    { month: 'Jan', allocated: 10000, used: 5400 },
    { month: 'Feb', allocated: 10000, used: 6200 },
    { month: 'Mar', allocated: 10000, used: 5720 },
  ];

  const tokenTimeline = [
    { day: 'Mon', tokens: 180000 },
    { day: 'Tue', tokens: 210000 },
    { day: 'Wed', tokens: 195000 },
    { day: 'Thu', tokens: 240000 },
    { day: 'Fri', tokens: 220000 },
    { day: 'Sat', tokens: 120000 },
    { day: 'Sun', tokens: 119000 },
  ];

  const maxMonthly = Math.max(...monthlyData.map((d) => d.allocated));
  const maxTokens = Math.max(...tokenTimeline.map((d) => d.tokens));

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Memory Analytics</h1>
            <p className="text-sm text-[#9CB0C7]">Memory Analytics</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              ← Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-8">
          <Database className="w-8 h-8 text-[#7A5CFF]" />
          <div>
            <h2 className="text-3xl font-bold text-white">Memory Store & Usage Analytics</h2>
            <p className="text-[#9CB0C7]">Credit balance, endpoint usage, token consumption, and entity state</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-[#111A2E] rounded-lg p-1 w-fit border border-[#22304A]">
          {['overview', 'usage', 'memory'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
                activeTab === tab ? 'bg-[#7A5CFF] text-white' : 'text-[#9CB0C7] hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Credit Balance + Gauge */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-1 rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Gauge className="w-5 h-5 text-[#3EF1C7]" />
                  <h3 className="text-lg font-semibold text-white">Credit Balance</h3>
                </div>
                {/* Gauge visualization */}
                <div className="flex items-center justify-center mb-4">
                  <div className="relative w-40 h-40">
                    <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                      <circle cx="60" cy="60" r="50" fill="none" stroke="#22304A" strokeWidth="10" />
                      <circle
                        cx="60" cy="60" r="50" fill="none"
                        stroke={creditPercent > 80 ? '#F55454' : creditPercent > 60 ? '#FFC857' : '#3EF1C7'}
                        strokeWidth="10"
                        strokeDasharray={`${creditPercent * 3.14} ${314 - creditPercent * 3.14}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-white">{creditPercent}%</span>
                      <span className="text-xs text-[#9CB0C7]">used</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#9CB0C7]">Remaining</span>
                    <span className="text-[#3EF1C7] font-semibold">{credits?.balance?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#9CB0C7]">Used</span>
                    <span className="text-white font-semibold">{credits?.used?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#9CB0C7]">Total</span>
                    <span className="text-white font-semibold">{credits?.total?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-[#22304A]">
                    <span className="text-[#9CB0C7]">Next Refill</span>
                    <span className="text-[#7A5CFF] font-semibold">{credits?.nextRefill}</span>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                {[
                  { label: 'Monthly Allocation', value: usage?.allocated?.toLocaleString(), icon: CreditCard, color: '#7A5CFF' },
                  { label: 'Current Usage', value: usage?.currentMonth?.toLocaleString(), icon: Activity, color: '#3EF1C7' },
                  { label: 'Outcome Fees', value: `$${usage?.outcomeFees?.toFixed(2)}`, icon: Coins, color: '#FFC857' },
                  { label: 'Tokens Consumed', value: `${(usage?.tokenConsumed / 1000).toFixed(0)}K`, icon: Server, color: '#7A5CFF' },
                ].map((stat, idx) => {
                  const Icon = stat.icon;
                  return (
                    <div key={idx} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                          <Icon className="w-4 h-4" style={{ color: stat.color }} />
                        </div>
                        <span className="text-sm text-[#9CB0C7]">{stat.label}</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Monthly Allocation vs Used */}
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-[#7A5CFF]" />
                <h3 className="text-lg font-semibold text-white">Monthly Allocation vs Used</h3>
              </div>
              <div className="flex items-end gap-4 h-48">
                {monthlyData.map((d, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex gap-1 items-end" style={{ height: '160px' }}>
                      <div
                        className="flex-1 rounded-t bg-[#22304A]"
                        style={{ height: `${(d.allocated / maxMonthly) * 100}%` }}
                      />
                      <div
                        className="flex-1 rounded-t bg-[#7A5CFF]"
                        style={{ height: `${(d.used / maxMonthly) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#9CB0C7]">{d.month}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#22304A]" />
                  <span className="text-xs text-[#9CB0C7]">Allocated</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#7A5CFF]" />
                  <span className="text-xs text-[#9CB0C7]">Used</span>
                </div>
              </div>
            </div>

            {/* Token Consumption Timeline */}
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-[#3EF1C7]" />
                <h3 className="text-lg font-semibold text-white">Token Consumption (This Week)</h3>
              </div>
              <div className="flex items-end gap-3 h-40">
                {tokenTimeline.map((d, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full rounded-t bg-gradient-to-t from-[#3EF1C7]/60 to-[#3EF1C7]"
                      style={{ height: `${(d.tokens / maxTokens) * 130}px` }}
                    />
                    <span className="text-xs text-[#9CB0C7]">{d.day}</span>
                    <span className="text-xs text-[#9CB0C7]">{(d.tokens / 1000).toFixed(0)}K</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'usage' && (
          <>
            {/* Usage Breakdown Table */}
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] overflow-hidden mb-8">
              <div className="p-4 border-b border-[#22304A] flex items-center gap-2">
                <Layers className="w-5 h-5 text-[#FFC857]" />
                <h3 className="text-lg font-semibold text-white">Usage Breakdown by Endpoint</h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#22304A] text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-[#9CB0C7] uppercase">Endpoint</th>
                    <th className="px-4 py-3 text-xs font-semibold text-[#9CB0C7] uppercase text-right">Calls</th>
                    <th className="px-4 py-3 text-xs font-semibold text-[#9CB0C7] uppercase text-right">Credits</th>
                    <th className="px-4 py-3 text-xs font-semibold text-[#9CB0C7] uppercase text-right">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((row, idx) => (
                    <tr key={idx} className="border-b border-[#22304A] last:border-0 hover:bg-[#0B1120]/50">
                      <td className="px-4 py-3">
                        <code className="text-sm text-[#3EF1C7] font-mono">{row.endpoint}</code>
                      </td>
                      <td className="px-4 py-3 text-sm text-white text-right">{row.calls.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-[#FFC857] font-semibold text-right">{row.credits.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Sparkline data={row.trend} color="#7A5CFF" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'memory' && (
          <>
            {/* Entity State Viewer */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Search className="w-5 h-5 text-[#7A5CFF]" />
                  <h3 className="text-lg font-semibold text-white">Entity State Viewer</h3>
                </div>
                <form onSubmit={handleEntitySearch} className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={entitySearch}
                    onChange={(e) => setEntitySearch(e.target.value)}
                    placeholder="Search entities..."
                    className="flex-1 px-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white placeholder-[#9CB0C7] text-sm focus:outline-none focus:border-[#7A5CFF]"
                  />
                  <button type="submit" className="px-4 py-2 rounded-lg bg-[#7A5CFF] text-white text-sm font-medium hover:bg-[#6B4FE0] transition-colors">
                    Search
                  </button>
                </form>
                {selectedEntity && (
                  <div className="p-4 rounded-lg bg-[#0B1120] border border-[#7A5CFF]/30">
                    <p className="text-sm font-semibold text-white mb-2">{selectedEntity.name}</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span className="text-[#9CB0C7]">Type</span><span className="text-white">{selectedEntity.type}</span></div>
                      <div className="flex justify-between"><span className="text-[#9CB0C7]">Records</span><span className="text-white">{selectedEntity.records}</span></div>
                      <div className="flex justify-between"><span className="text-[#9CB0C7]">Last Access</span><span className="text-[#3EF1C7]">{selectedEntity.lastAccess}</span></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Domain Memory Browser */}
              <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <HardDrive className="w-5 h-5 text-[#3EF1C7]" />
                  <h3 className="text-lg font-semibold text-white">Domain Memory Browser</h3>
                  <span className="text-xs text-[#9CB0C7] ml-auto">{memorySummary?.storageUsed} used</span>
                </div>
                <div className="space-y-3">
                  {memorySummary?.entities?.map((entity) => (
                    <div
                      key={entity.id}
                      onClick={() => setSelectedEntity(entity)}
                      className="flex items-center justify-between p-3 rounded-lg bg-[#0B1120] border border-[#22304A] cursor-pointer hover:border-[#7A5CFF]/50 transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">{entity.name}</p>
                        <p className="text-xs text-[#9CB0C7]">{entity.records} records</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#3EF1C7]">{entity.lastAccess}</p>
                        <p className="text-xs text-[#9CB0C7]">{entity.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-[#22304A] flex justify-between text-xs text-[#9CB0C7]">
                  <span>Total entities: {memorySummary?.totalEntities?.toLocaleString()}</span>
                  <span>{memorySummary?.domains} domains</span>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
